import { useIsMobile } from '@ben/orion/hooks/use-is-mobile';
import type { Row } from '@tanstack/react-table';
import { t } from 'i18next';
import { partition } from 'lodash';
import styled, { css } from 'styled-components';
import ActionsPopover from '../../../actions-popover/actions-popover';
import InlineTextButton from '../../../inline-text-button/inline-text-button';
import type { TableAction } from '../../types';

interface ActionsCellProps<T> {
  actions?: TableAction<T>[];
  row: Row<T>;
  isDisabled?: boolean;
}
export const ActionsCell = <T,>({
  actions = [],
  row,
  isDisabled,
}: ActionsCellProps<T>) => {
  const isMobile = useIsMobile();

  const filteredActions = actions.filter((action) =>
    typeof action.shouldShow === 'function'
      ? action.shouldShow(row.original)
      : action.shouldShow !== false
  );

  const resolveActionProperty = <V,>(value: V | ((item: T) => V)): V =>
    typeof value === 'function'
      ? (value as (item: T) => V)(row.original)
      : value;

  const getLabel = (action: TableAction<T>) =>
    resolveActionProperty(action.label);

  const getDisabledState = (action: TableAction<T>) =>
    resolveActionProperty(action.disabled) === true;

  const getDisabledTooltip = (action: TableAction<T>) =>
    resolveActionProperty(action.disabledTooltip);

  const isActionButtonDisabled = actions?.every((action) =>
    getDisabledState(action)
  );

  const disabled = isDisabled || isActionButtonDisabled;

  if (filteredActions.length <= 1 && !isMobile) {
    return (
      <Wrapper>
        {filteredActions.map((action) => (
          <InlineTextButton
            key={getLabel(action)}
            label={getLabel(action)}
            onClick={() => action.onClick(row.original)}
            isUnderlined
            aria-label={action.ariaLabel}
          />
        ))}
      </Wrapper>
    );
  }

  const [primaryActions, secondaryActions] = isMobile
    ? [[], filteredActions] // Show actions in popover on mobile
    : partition(filteredActions, 'primary');

  return (
    <Wrapper>
      {primaryActions.map((action) => (
        <InlineTextButton
          key={getLabel(action)}
          label={getLabel(action)}
          onClick={() => action.onClick(row.original)}
          isUnderlined
          aria-label={action.ariaLabel}
        />
      ))}
      {secondaryActions.length > 0 && (
        <ActionsPopover
          actions={secondaryActions.map((action) => {
            const isDisabled = getDisabledState(action);
            return {
              label: getLabel(action),
              handleClick: () => action.onClick(row.original),
              shouldShow: true, // We already filtered the actions above so this can just be true
              ariaLabel: action.ariaLabel,
              isDisabled,
              disabledTooltip: getDisabledTooltip(action),
            };
          })}
          popoverAriaLabel={t('phrases.moreActions')}
          tooltipText={disabled ? undefined : t('phrases.moreActions')}
          isDisabled={disabled}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: ${theme.spacing(3)};
  `}
`;
